import InfoIcon from "@mui/icons-material/Info";
import MailIcon from "@mui/icons-material/Mail";
import MenuIcon from "@mui/icons-material/Menu";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import abc from "./images/abc.jpg";
import welcome from "./images/welcome.jpg";
import Logotransparent from "./Logotransparent.png";

const Welcome = () => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const navItems = [
    {
      label: "About Us",
      icon: <InfoIcon />,
      onClick: () => navigate("/about"),
    },
    {
      label: "Services",
      icon: <VisibilityIcon />,
      onClick: () =>
        document.getElementById("features")?.scrollIntoView({
          behavior: "smooth",
        }),
    },
    {
      label: "Contact",
      icon: <MailIcon />,
      onClick: () =>
        document.getElementById("contact")?.scrollIntoView({
          behavior: "smooth",
        }),
    },
  ];

  return (
    <Box
      sx={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55)), url(${welcome})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: "white",
        pb: { xs: 6, sm: 8 },
      }}
    >
      {/* --- NAVIGATION BAR --- */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "black",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.6)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: { xs: 2, sm: 4 },
          }}
        >
          {/* --- LEFT SIDE: Logo + Name --- */}
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <img
              src={Logotransparent}
              alt="Ophthalmo Corp Logo"
              style={{
                width: "40px",
                height: "40px",
                marginRight: "10px",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                letterSpacing: "1px",
                fontFamily: "'Poppins', sans-serif",
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
              }}
            >
              Ophthalmo Corp.
            </Typography>
          </Box>

          {/* --- DESKTOP NAVIGATION --- */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "10px" }}>
            {navItems.map((item) => (
              <Button key={item.label} color="inherit" onClick={item.onClick}>
                {item.label}
              </Button>
            ))}
          </Box>

          {/* --- MOBILE MENU ICON --- */}
          <IconButton
            sx={{
              display: { xs: "flex", md: "none" },
              color: "white",
              transition: "transform 0.2s ease",
              "&:active": { transform: "scale(0.9)" },
            }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon fontSize="large" />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* --- MOBILE DRAWER --- */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 250,
            background:
              "linear-gradient(135deg, rgba(0,0,0,0.9), rgba(0,188,212,0.25))",
            color: "white",
            backdropFilter: "blur(8px)",
            boxShadow: "0px 4px 30px rgba(0,0,0,0.5)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 3,
            gap: 2,
          }}
        >
          {/* --- Drawer Header --- */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              cursor: "pointer",
            }}
            onClick={() => {
              navigate("/");
              setDrawerOpen(false);
            }}
          >
            <img
              src={Logotransparent}
              alt="Logo"
              style={{ width: 40, height: 40, marginRight: 8 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "0.5px",
              }}
            >
              Ophthalmo Corp.
            </Typography>
          </Box>

          {/* --- Navigation Links --- */}
          <List sx={{ width: "100%" }}>
            {navItems.map((item) => (
              <ListItem
                key={item.label}
                onClick={() => {
                  item.onClick();
                  setDrawerOpen(false);
                }}
                sx={{
                  py: 1.5,
                  px: 3,
                  cursor: "pointer", // 👈 Added this line
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0,188,212,0.25)",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    backgroundColor: "rgba(0,188,212,0.35)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "#00e5ff", minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: "1.1rem",
                      fontWeight: 500,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="body2"
            sx={{
              color: "#b2ebf2",
              mt: 3,
              fontStyle: "italic",
              fontSize: "0.9rem",
            }}
          >
            “A World Where Everyone Sees”
          </Typography>
        </Box>
      </Drawer>

      {/* --- WELCOME SECTION --- */}
      <Box
        sx={{
          background: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(6px)",
          p: { xs: 4, sm: 6 },
          borderRadius: "20px",
          textAlign: "left",
          mt: { xs: "90px", md: "100px" },
          width: { xs: "90%", md: "70%" },
          boxShadow: "0px 4px 40px rgba(0,188,212,0.25)",
          transition: "transform 0.4s ease",
          "&:hover": { transform: "scale(1.02)" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 3, md: 4 },
        }}
      >
        {/* Left Side - Text */}
        <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(90deg, #00e5ff, #80deea)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: "1px",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            “A World Where Everyone Sees”
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontStyle: "italic",
              color: "#b2ebf2",
              mb: 2,
              fontFamily: "'Montserrat', sans-serif",
              textShadow: "0px 2px 10px rgba(0,0,0,0.4)",
              fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
            }}
          ></Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
              lineHeight: 1.8,
              color: "#e0f7fa",
              fontFamily: "'Open Sans', sans-serif",
              maxWidth: "700px",
              mx: { xs: "auto", md: 0 },
              textShadow: "0px 2px 6px rgba(0,0,0,0.6)",
            }}
          >
            Empowering eye doctors through intelligent automation — freeing them
            from administrative burdens so they can focus on what truly matters:
            providing exceptional care and preserving the gift of sight.
          </Typography>
        </Box>

        {/* Right Side - Image */}
        <Box
          component="img"
          src={abc}
          alt="Eye illustration"
          sx={{
            width: { xs: "100%", sm: "80%", md: "450px", lg: "500px" },
            borderRadius: "12px",
            boxShadow: "0px 4px 30px rgba(0,188,212,0.3)",
            mt: { xs: 3, md: 0 },
          }}
        />
      </Box>

      {/* --- ABOUT SECTION --- */}
      <Container
        id="about"
        sx={{
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(8px)",
          p: { xs: 4, sm: 5, md: 6 },
          mt: { xs: 6, md: 10 },
          borderRadius: "15px",
          color: "#212121",
          boxShadow: "0px 6px 30px rgba(0,188,212,0.25)",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "#00acc1",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          About Ophthalmo Corp.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          paragraph
          sx={{
            fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
            lineHeight: 1.8,
            color: "#424242",
            maxWidth: "870px",
            // minWidth: "300px",
            mx: "auto",
          }}
        >
          Eyesight is often cited as the most important sensory mechanism that
          humans posses. Ophthalmo Corp. is a STEM-based AI company that
          envision a world where everyone sees, and they do so with the clearest
          vision.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          paragraph
          sx={{
            fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
            lineHeight: 1.8,
            color: "#424242",
            maxWidth: "870px",
            mx: "auto",
          }}
        >
          Canada proudly offers accessible healthcare to all Canadians. Our
          country’s public healthcare system now more than ever can benefit
          immensely from smarter healthcare that lifts the burden off our
          medical professionals, especially doctors, without incurring
          insurmountable cost to the public, and we found it the right time to
          contribute to this important cause.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{
            fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
            lineHeight: 1.8,
            color: "#424242",
            maxWidth: "850px",
            mx: "auto",
          }}
        >
          {/* Proudly based in Canada, we are dedicated to advancing accessible,
          intelligent, and ethical healthcare powered by next-generation AI
          built with the highest standards of privacy and security. */}
        </Typography>
      </Container>

      {/* --- VISION SECTION --- */}
      <Container
        id="features"
        sx={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(8px)",
          p: { xs: 4, sm: 5, md: 6 },
          mt: { xs: 6, md: 8 },
          borderRadius: "15px",
          color: "#212121",
          boxShadow: "0px 6px 30px rgba(0,188,212,0.25)",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "#00acc1",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          What We Offer
        </Typography>

        <Typography
          variant="body1"
          align="center"
          paragraph
          sx={{
            color: "#424242",
            fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
            maxWidth: "850px",
            mx: "auto",
            mb: 3,
          }}
        >
          In today’s healthcare, physicians are burdened by numerous
          administrative tasks. Our team consists of medical and technology
          professionals aspiring to provide assistive tools and technology to
          physicians enabling them to concentrate on the medical task at hand
          and offer the highest quality of eyecare to their patients.
        </Typography>

        {/* <ul
          style={{
            textAlign: "left",
            maxWidth: "750px",
            margin: "0 auto",
            fontSize: "1.05rem",
            color: "#333",
            lineHeight: "1.9",
          }}
        >
          <li>
            <strong>Short-term(Today):</strong> Free eye doctors from
            administrative burden through intelligent automation.
          </li>
          <li>
            <strong>Mid-term (5–10 years):</strong> Achieve full automation
            across clinical workflows — where AI anticipates needs and works
            hand-in-hand with clinicians.
          </li>
          <li>
            <strong>Long-term (10–50+ years):</strong> Reverse the growth of
            blindness and make eyesight fully recoverable, regardless of cause.
          </li>
        </ul>

        <Typography
          variant="body1"
          align="center"
          sx={{
            mt: 3,
            fontStyle: "italic",
            color: "#004d40",
          }}
        >
          <SmartToyIcon fontSize="small" sx={{ verticalAlign: "middle" }} />{" "}
          Powered by the latest AI Technology
        </Typography> */}
      </Container>

      {/* --- CONTACT SECTION --- */}
      <Container
        id="contact"
        sx={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(8px)",
          p: { xs: 4, sm: 5, md: 6 },
          mt: { xs: 6, md: 8 },
          borderRadius: "15px",
          color: "#212121",
          boxShadow: "0px 4px 25px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "#00acc1",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Contact Ophthalmo Corp.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ fontSize: "1.05rem", color: "#424242", mt: 1 }}
        >
          📍 Toronto, Canada
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ fontSize: "1.05rem", color: "#424242" }}
        >
          📧 info@ophthalmo.ca
        </Typography>
      </Container>
    </Box>
  );
};

export default Welcome;
